import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { useEffect, useState } from "react";
import { useAutoSave } from "@/hooks/useAutoSave";
import * as z from "zod";

// Create a simple schema just for this form's fields
const formSchema = z.object({
  instructions: z.string().optional(),
  restrictions: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

type GeneralInstructionsFormProps = {
  agent: any;
  updateAgent: (data: Partial<any>) => Promise<boolean>;
  smallerHeight?: boolean;
};

const GeneralInstructionsForm = ({
  agent,
  updateAgent,
  smallerHeight = false,
}: GeneralInstructionsFormProps) => {
  const [initialFormValues, setInitialFormValues] = useState<FormValues | null>(
    {
      instructions: agent.instructions || "",
      restrictions: agent.restrictions || "",
    }
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      instructions: agent.instructions || "",
      restrictions: agent.restrictions || "",
    },
  });

  const { isSaving, setDirty } = useAutoSave({
    onSave: async () => {
      const data = form.getValues();
      const updateData = smallerHeight
        ? { instructions: data.instructions } // Only update instructions if smaller height
        : {
            instructions: data.instructions,
            restrictions: data.restrictions,
          };
      return await updateAgent(updateData);
    },
    successMessage: "Settings saved automatically",
    errorMessage: "Failed to auto-save settings",
  });

  // Set up form change subscriptions for auto-save
  useEffect(() => {
    const subscription = form.watch(() => {
      if (!initialFormValues) return;
      // Only set dirty if there are actual changes
      const currentValues = form.getValues();
      const hasChanges = Object.keys(currentValues).some(
        (key) =>
          currentValues[key as keyof FormValues] !==
          initialFormValues[key as keyof FormValues]
      );
      if (hasChanges) {
        setInitialFormValues(currentValues);
        setDirty();
      }
    });
    return () => subscription.unsubscribe();
  }, [form, setDirty, initialFormValues]);

  const textareaHeight = smallerHeight ? "min-h-24" : "min-h-32";

  return (
    <Form {...form}>
      <form className="space-y-6">
        <FormField
          control={form.control}
          name="instructions"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-normal text-gray-700">
                Main goals and general behavior for your agent
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Describe the main goals and general behavior for your agent"
                  className={`${textareaHeight} focus:border-[#060520] focus:ring-1 focus:ring-[#060520]`}
                  {...field}
                  value={field.value || ""}
                />
              </FormControl>
              <FormMessage className="text-sm text-[#666]" />
            </FormItem>
          )}
        />

        {!smallerHeight && (
          <FormField
            control={form.control}
            name="restrictions"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-normal text-gray-700">
                  What your agent can't or shouldn't do (optional)
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Describe what your agent can't or shouldn't do"
                    className="min-h-32 focus:border-[#060520] focus:ring-1 focus:ring-[#060520]"
                    {...field}
                    value={field.value || ""}
                  />
                </FormControl>
                <FormMessage className="text-sm text-[#666]" />
              </FormItem>
            )}
          />
        )}

        {isSaving && (
          <div className="flex items-center justify-end">
            <span className="text-sm text-[#666] flex items-center">
              <div className="h-3 w-3 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
              Auto-saving...
            </span>
          </div>
        )}
      </form>
    </Form>
  );
};

export default GeneralInstructionsForm;
